/**
 * src/services/spatial/geoLinkSpatialObjectsAdminClient.ts
 *
 * Admin / wallet-provider write client for the GeoLink spatial-objects
 * registry. Mirrors §1.3 of `GEOLINK_GEOTRUST_INTEGRATION.md` and
 * §2 of `GEOLINK_GEOTRUST_UI_INTEGRATION.md`.
 *
 * Endpoint surface this client exposes:
 *
 *   GET    /api/admin/spatial-objects                       → list (filters)
 *   GET    /api/admin/spatial-objects/:id                   → single
 *   POST   /api/admin/spatial-objects                       → create
 *   PATCH  /api/admin/spatial-objects/:id                   → update
 *   DELETE /api/admin/spatial-objects/:id                   → delete
 *   POST   /api/admin/spatial-objects/:id/link-rule         → link/unlink rule
 *   GET    /api/admin/spatial-objects/preview?provider=…    → admin-scoped preview
 *   POST   /api/admin/spatial-objects/:id/test-interaction  → dry-run interaction
 *   GET    /api/admin/spatial-objects/:id/agent             → AI presence config
 *   PUT    /api/admin/spatial-objects/:id/agent             → save AI presence config
 *   GET    /api/admin/spatial-objects/:id/media             → media presence config
 *   PUT    /api/admin/spatial-objects/:id/media             → save media presence config
 *   POST   /api/admin/spatial-objects/:id/media-assets      → attach a recording
 *   DELETE /api/admin/spatial-objects/:id/media-assets/:aid → detach a recording
 *   GET    /api/contracts/rules                              → list rules (auto-fill dropdown)
 *
 * Auth model (per the integration guide): every write is keyed by an
 * `X-API-Key` wallet-provider key. The server resolves the caller's
 * `user_id` from the key and:
 *   - Defaults `owner_wallet` to the caller's public_key when omitted.
 *   - Refuses mutations on objects whose owner_wallet is not one of
 *     the caller's wallets (403 forbidden) — unless the caller is admin.
 *
 * GeoTrust never falls back to mock data; calls that fail surface real
 * errors so the admin UI can show meaningful messages.
 */

import {
  getGeoLinkApiBaseUrl,
  getSpatialRenderProvider,
} from './spatialEnv';
import { adminListFiltersServerSide } from './geoLinkCapabilities';
import type {
  ContractExecutionRuleSummary,
  SpatialObjectAdminCreateBody,
  SpatialObjectAdminUpdateBody,
  SpatialObjectFeature,
  SpatialObjectInteractResponse,
  SpatialObjectLinkRuleBody,
  SpatialObjectTestInteractionBody,
  SpatialObjectsRenderEnvelope,
  SpatialObjectType,
  SpatialObjectVisibility,
  SpatialRenderProvider,
} from './geoLinkTypes';
import type {
  SpatialObjectAgentConfig,
  SpatialObjectAgentConfigPatch,
  SpatialObjectMediaAsset,
  SpatialObjectMediaConfig,
  SpatialObjectMediaConfigPatch,
  SpatialObjectSurfaceConfig,
  SpatialObjectSurfaceConfigPatch,
} from './spatialPresenceTypes';

/** One configured surface belonging to an object, as GeoLink lists them. */
export interface SpatialSurfaceSummary {
  objectId: string;
  name: string | null;
  objectType: SpatialObjectType;
  parentObjectId: string | null;
  centroid: { type: 'Point'; coordinates: [number, number] } | null;
  config: Pick<
    SpatialObjectSurfaceConfig,
    | 'enabled'
    | 'defaultContentRef'
    | 'activationMode'
    | 'executionRuleId'
    | 'maxActivationSeconds'
    | 'cooldownSeconds'
    | 'allowedContentRefs'
    | 'surfaceKind'
    | 'authorizedByWallet'
  >;
}

/** Sort columns accepted by GET /api/admin/spatial-objects. */
export type SpatialObjectAdminSort = 'created_at' | 'updated_at' | 'name' | 'distance';

/** Filter parameters supported by GET /api/admin/spatial-objects. */
export interface SpatialObjectAdminListQuery {
  object_type?: SpatialObjectType;
  owner_wallet?: string;
  parent_object_id?: string;
  linked_execution_rule_id?: number | string;
  visibility?: SpatialObjectVisibility;
  bbox?: { sw: { lng: number; lat: number }; ne: { lng: number; lat: number } };
  near?: { lng: number; lat: number; radius_meters: number };
  /**
   * Text search over name, description, and id prefix. GeoLink ignores
   * terms shorter than 2 characters rather than erroring, so a
   * per-keystroke search box returns an unfiltered page for the first
   * character — debounce, and treat 1-character input as "no filter".
   */
  q?: string;
  country_code?: string;
  organization_id?: string;
  /**
   * `distance` is only honoured alongside `near`; without a point to
   * measure from GeoLink silently falls back to `created_at`.
   */
  sort?: SpatialObjectAdminSort;
  sort_dir?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

/**
 * Admin list response.
 *
 * `serverFiltered` is the important field. Deployments predating the
 * 2026-07-30 GeoLink change ignore `bbox`, `q` and friends *silently* —
 * unknown query parameters are dropped and unknown `sort` values fall
 * back to the default — so a caller that assumed server-side filtering
 * would render a plausible but wrong list.
 *
 * It is answered by `GET /api/capabilities`, not by guessing from the
 * response shape, and it is only true when we actually sent a filter
 * for GeoLink to apply.
 */
export interface SpatialObjectAdminPage {
  rows: SpatialObjectAdminRow[];
  /** Size of the filtered set ignoring limit/offset. Null on older builds. */
  total: number | null;
  /**
   * True when GeoLink applied the geometry/search filters itself, so the
   * caller must NOT re-filter in memory.
   */
  serverFiltered: boolean;
}

/**
 * Slim object shape returned by the admin list endpoint. GeoLink
 * mostly returns camelCase here, but some builds return snake_case;
 * we surface both via the convenience `feature()` helper below.
 */
export interface SpatialObjectAdminRow {
  id: string;
  objectType: SpatialObjectType;
  name?: string | null;
  description?: string | null;
  ownerWallet?: string | null;
  visibility?: SpatialObjectVisibility;
  geometry?: SpatialObjectFeature['geometry'];
  centroid?: { type: 'Point'; coordinates: [number, number] };
  baseHeightMeters?: number;
  heightMeters?: number;
  floorNumber?: number | null;
  parentObjectId?: string | null;
  /** ISO 3166-1 alpha-2, upper-case. Absent on GeoLink builds before 2026-07-30. */
  countryCode?: string | null;
  /** 64-char lowercase hex `geo-org-registry` id. Absent on older builds. */
  organizationId?: string | null;
  metadata?: Record<string, unknown> | null;
  linkedExecutionRuleId?: number | string | null;
  linkedContractAddress?: string | null;
  linkedContractFunction?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Whether this GeoLink routes by-id reads is a property of the deployment,
 * not of the page, so the answer is remembered for the tab. Without this
 * every reload re-probed and logged a 404 the user had to learn to ignore.
 */
const BY_ID_ABSENT_KEY = 'geolink.spatialAdmin.byIdRouteAbsent';

function readByIdRouteAbsent(): boolean {
  try {
    return window.sessionStorage.getItem(BY_ID_ABSENT_KEY) === '1';
  } catch {
    return false;
  }
}

function writeByIdRouteAbsent(): void {
  try {
    window.sessionStorage.setItem(BY_ID_ABSENT_KEY, '1');
  } catch { /* private mode / no storage — re-probe next load */ }
}

class GeoLinkSpatialObjectsAdminClient {
  private baseUrl: string;
  private walletProviderKey: string | null;
  /** Optional consumer key fallback for read endpoints. */
  private apiKey: string | null;

  /**
   * Set once this deployment answers 404 for a by-id read. Static because
   * it is a fact about the server, not about a client instance, and one
   * probe should spare every later caller the same wasted round trip.
   */
  private static byIdRouteAbsent = readByIdRouteAbsent();

  /**
   * The first by-id read of a session, shared so a burst of lookups probes
   * once rather than each logging its own 404 to the console.
   */
  private static byIdProbe: Promise<void> | null = null;

  private static listCache: { at: number; rows: SpatialObjectAdminRow[] } | null = null;

  constructor(opts: {
    baseUrl?: string;
    walletProviderKey?: string | null;
    apiKey?: string | null;
  } = {}) {
    this.baseUrl = (opts.baseUrl ?? getGeoLinkApiBaseUrl()).replace(/\/+$/, '');
    this.walletProviderKey = opts.walletProviderKey ?? null;
    this.apiKey = opts.apiKey ?? null;
  }

  // ─── Internal HTTP helper ─────────────────────────────────────────

  /**
   * Browser callers send NO auth header: `getGeoLinkApiBaseUrl()` returns the
   * GeoTrust backend proxy, which injects the server-side key and discards
   * anything the browser sent. Keys only appear when a Node/test caller passes
   * them explicitly to the constructor and talks to GeoLink directly.
   */
  private async request<T>(
    path: string,
    init: RequestInit & {
      preferConsumerKey?: boolean;
    } = {},
  ): Promise<T> {
    const key = init.preferConsumerKey
      ? (this.apiKey ?? this.walletProviderKey)
      : (this.walletProviderKey ?? this.apiKey);

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(init.headers as Record<string, string> | undefined),
    };
    if (key && key.trim()) {
      headers['X-API-Key'] = key.trim();
    }

    const url = `${this.baseUrl}${path.startsWith('/') ? path : '/' + path}`;
    const fetchOpts: RequestInit = { ...init, headers };
    delete (fetchOpts as { preferConsumerKey?: boolean }).preferConsumerKey;

    const res = await fetch(url, fetchOpts);
    const text = await res.text();
    let body: unknown = null;
    if (text) {
      try { body = JSON.parse(text); } catch { /* leave as text */ }
    }
    if (!res.ok) {
      const b = (body ?? {}) as { error?: string; reason?: string; message?: string; details?: string };
      const msg = b.reason || b.error || b.message || `${res.status} ${res.statusText}`;
      // GeoLink names the failing check constraint in `details`. Dropping
      // it leaves refusals like `invalid_configuration` saying only that
      // something in the payload was wrong, which is a puzzle, not a report.
      const full = b.details && b.details !== msg ? `${msg} (${b.details})` : msg;
      const err = new Error(`[geoLinkSpatialAdmin] ${path}: ${full}`);
      (err as { status?: number; details?: string }).status = res.status;
      (err as { status?: number; details?: string }).details = b.details;
      throw err;
    }
    return (body as T) ?? (undefined as unknown as T);
  }

  // ─── List / read ──────────────────────────────────────────────────

  /**
   * `GET /api/admin/spatial-objects` — list with admin scope. Wallet-
   * provider keys see their own + public objects; admin JWTs see all.
   */
  async listObjects(query: SpatialObjectAdminListQuery = {}): Promise<SpatialObjectAdminRow[]> {
    return (await this.listObjectsPage(query)).rows;
  }

  /**
   * As `listObjects`, but also reports the unpaged `total` and whether
   * GeoLink actually applied the geometry/search/sort filters. Callers
   * that pass `bbox`, `q` or `sort` MUST use this and honour
   * `serverFiltered` — an older deployment drops those parameters
   * without complaint.
   */
  async listObjectsPage(query: SpatialObjectAdminListQuery = {}): Promise<SpatialObjectAdminPage> {
    const qs = new URLSearchParams();
    if (query.object_type)               qs.set('object_type', query.object_type);
    if (query.owner_wallet)              qs.set('owner_wallet', query.owner_wallet);
    if (query.parent_object_id)          qs.set('parent_object_id', query.parent_object_id);
    if (query.linked_execution_rule_id != null) {
      qs.set('linked_execution_rule_id', String(query.linked_execution_rule_id));
    }
    if (query.visibility)                qs.set('visibility', query.visibility);
    if (query.bbox) {
      const { sw, ne } = query.bbox;
      qs.set('bbox', `${sw.lng.toFixed(6)},${sw.lat.toFixed(6)},${ne.lng.toFixed(6)},${ne.lat.toFixed(6)}`);
    }
    if (query.near) {
      qs.set('near',   `${query.near.lng.toFixed(6)},${query.near.lat.toFixed(6)}`);
      qs.set('radius', String(query.near.radius_meters));
    }
    // GeoLink ignores terms under 2 characters; sending them anyway
    // would make an old build (which ignores `q` entirely) and a new one
    // agree, but it costs a wasted round trip on every first keystroke.
    const term = (query.q ?? '').trim();
    if (term.length >= 2) qs.set('q', term);
    if (query.country_code)    qs.set('country_code', query.country_code.toUpperCase());
    if (query.organization_id) qs.set('organization_id', query.organization_id.toLowerCase());
    if (query.sort)     qs.set('sort', query.sort);
    if (query.sort_dir) qs.set('sort_dir', query.sort_dir);
    if (query.limit  != null) qs.set('limit',  String(query.limit));
    if (query.offset != null) qs.set('offset', String(query.offset));

    // Only these narrow the set beyond what an old deployment already
    // honours, so only these decide whether the caller may skip its own
    // filtering pass.
    const sentServerFilter = qs.has('bbox') || qs.has('q') || qs.has('country_code');

    const path = `/api/admin/spatial-objects${qs.toString() ? `?${qs.toString()}` : ''}`;
    const [json, filtersSupported] = await Promise.all([
      this.request<{
        total?: number;
        count?: number;
        objects?: SpatialObjectAdminRow[];
        spatialObjects?: SpatialObjectAdminRow[];
        data?: SpatialObjectAdminRow[];
      }>(path),
      sentServerFilter ? adminListFiltersServerSide() : Promise.resolve(false),
    ]);

    const rows = json.objects ?? json.spatialObjects ?? json.data ?? [];
    const total = typeof json.total === 'number' ? json.total : null;
    return { rows, total, serverFiltered: sentServerFilter && filtersSupported };
  }

  /**
   * `GET /api/admin/spatial-objects/:id` — single canonical object.
   *
   * Some GeoLink deployments do not route this path, and a 404 cannot
   * distinguish "no such object" from "no such route". Returning null for
   * both was wrong in a way that mattered: `mirrorObjectOrganization`
   * reads the row to merge metadata, and reported a perfectly real object
   * as one GeoLink had never heard of.
   *
   * So the first 404 latches, and every later lookup is answered from the
   * list — which does exist everywhere, and can tell the two apart. The
   * list is cached briefly because the map layer resolves several objects
   * in a burst and one page answers all of them.
   */
  async getObject(id: string): Promise<SpatialObjectAdminRow | null> {
    // Wait behind a probe already in flight, so only the first of a burst
    // pays the failed round trip.
    const inFlight = GeoLinkSpatialObjectsAdminClient.byIdProbe;
    if (inFlight && !GeoLinkSpatialObjectsAdminClient.byIdRouteAbsent) {
      await inFlight.catch(() => undefined);
    }

    if (!GeoLinkSpatialObjectsAdminClient.byIdRouteAbsent) {
      const attempt = this.request<SpatialObjectAdminRow | { object?: SpatialObjectAdminRow }>(
        `/api/admin/spatial-objects/${encodeURIComponent(id)}`,
      );
      GeoLinkSpatialObjectsAdminClient.byIdProbe = attempt.then(() => undefined, () => undefined);
      try {
        const json = await attempt;
        return (json as { object?: SpatialObjectAdminRow }).object ?? (json as SpatialObjectAdminRow);
      } catch (e) {
        if ((e as { status?: number }).status !== 404) throw e;
        GeoLinkSpatialObjectsAdminClient.byIdRouteAbsent = true;
        writeByIdRouteAbsent();
      }
    }
    const rows = await this.recentObjects();
    return rows.find((r) => r.id === id) ?? null;
  }

  /** The object list, reused for a few seconds across a burst of lookups. */
  private async recentObjects(): Promise<SpatialObjectAdminRow[]> {
    const cached = GeoLinkSpatialObjectsAdminClient.listCache;
    if (cached && Date.now() - cached.at < 10_000) return cached.rows;

    const rows = await this.listObjects({ limit: 1000 });
    GeoLinkSpatialObjectsAdminClient.listCache = { at: Date.now(), rows };
    return rows;
  }

  // ─── Create / update / delete ─────────────────────────────────────

  async createObject(body: SpatialObjectAdminCreateBody): Promise<SpatialObjectAdminRow> {
    const json = await this.request<{ object?: SpatialObjectAdminRow } | SpatialObjectAdminRow>(
      '/api/admin/spatial-objects',
      { method: 'POST', body: JSON.stringify(body) },
    );
    return (json as { object?: SpatialObjectAdminRow }).object ?? (json as SpatialObjectAdminRow);
  }

  async updateObject(id: string, body: SpatialObjectAdminUpdateBody): Promise<SpatialObjectAdminRow> {
    const json = await this.request<{ object?: SpatialObjectAdminRow } | SpatialObjectAdminRow>(
      `/api/admin/spatial-objects/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    );
    return (json as { object?: SpatialObjectAdminRow }).object ?? (json as SpatialObjectAdminRow);
  }

  async deleteObject(id: string): Promise<{ deleted: boolean }> {
    const json = await this.request<{ deleted?: boolean; ok?: boolean }>(
      `/api/admin/spatial-objects/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    );
    return { deleted: !!(json.deleted ?? json.ok) };
  }

  /**
   * `POST /api/admin/spatial-objects/:id/link-rule` — link or unlink an
   * execution rule and optionally a direct contract. Pass null/undefined
   * fields to UNlink. Mirrors §2.5–§2.6 of the UI guide.
   */
  async linkRule(id: string, body: SpatialObjectLinkRuleBody): Promise<SpatialObjectAdminRow | null> {
    const json = await this.request<{ object?: SpatialObjectAdminRow } | SpatialObjectAdminRow | null>(
      `/api/admin/spatial-objects/${encodeURIComponent(id)}/link-rule`,
      { method: 'POST', body: JSON.stringify(body) },
    );
    if (!json) return null;
    return (json as { object?: SpatialObjectAdminRow }).object ?? (json as SpatialObjectAdminRow);
  }

  // ─── Provider preview + interaction tester ────────────────────────

  /**
   * `GET /api/admin/spatial-objects/preview?provider=…` — admin-scoped
   * render envelope. When `objectId` is provided, returns a single-
   * feature collection.
   */
  async previewRender(opts: {
    provider?: SpatialRenderProvider;
    objectId?: string;
  } = {}): Promise<SpatialObjectsRenderEnvelope> {
    const provider = opts.provider ?? getSpatialRenderProvider();
    const qs = new URLSearchParams({ provider });
    if (opts.objectId) qs.set('object_id', opts.objectId);
    return this.request<SpatialObjectsRenderEnvelope>(
      `/api/admin/spatial-objects/preview?${qs.toString()}`,
    );
  }

  /**
   * `POST /api/admin/spatial-objects/:id/test-interaction` — dry-run an
   * /interact decision without recording it or authorising execution.
   * Same response shape as /interact, perfect for an admin "would this
   * verb be allowed for this wallet?" preview.
   */
  async testInteraction(
    id: string,
    body: SpatialObjectTestInteractionBody,
  ): Promise<SpatialObjectInteractResponse> {
    return this.request<SpatialObjectInteractResponse>(
      `/api/admin/spatial-objects/${encodeURIComponent(id)}/test-interaction`,
      { method: 'POST', body: JSON.stringify(body) },
    );
  }

  // ─── Presence configuration (AI + media) ──────────────────────────

  /**
   * `GET /api/admin/spatial-objects/:id/agent`
   *
   * Null means the object has no AI presence at all, which is a
   * different state from a saved-but-disabled configuration: the form
   * shows defaults for the former and the stored values for the latter.
   */
  async getAgentConfig(id: string): Promise<SpatialObjectAgentConfig | null> {
    const json = await this.request<{ config: SpatialObjectAgentConfig | null }>(
      `/api/admin/spatial-objects/${encodeURIComponent(id)}/agent`,
    );
    return json.config ?? null;
  }

  /** `PUT /api/admin/spatial-objects/:id/agent` — partial update. */
  async putAgentConfig(
    id: string,
    patch: SpatialObjectAgentConfigPatch,
  ): Promise<SpatialObjectAgentConfig> {
    const json = await this.request<{ config: SpatialObjectAgentConfig }>(
      `/api/admin/spatial-objects/${encodeURIComponent(id)}/agent`,
      { method: 'PUT', body: JSON.stringify(patch) },
    );
    return json.config;
  }

  /** `GET /api/admin/spatial-objects/:id/media` — config plus attached assets. */
  async getMediaConfig(id: string): Promise<{
    config: SpatialObjectMediaConfig | null;
    assets: SpatialObjectMediaAsset[];
  }> {
    const json = await this.request<{
      config: SpatialObjectMediaConfig | null;
      assets?: SpatialObjectMediaAsset[];
    }>(`/api/admin/spatial-objects/${encodeURIComponent(id)}/media`);
    return { config: json.config ?? null, assets: json.assets ?? [] };
  }

  /** `PUT /api/admin/spatial-objects/:id/media` — partial update. */
  async putMediaConfig(
    id: string,
    patch: SpatialObjectMediaConfigPatch,
  ): Promise<SpatialObjectMediaConfig> {
    const json = await this.request<{ config: SpatialObjectMediaConfig }>(
      `/api/admin/spatial-objects/${encodeURIComponent(id)}/media`,
      { method: 'PUT', body: JSON.stringify(patch) },
    );
    return json.config;
  }

  /**
   * `GET /api/admin/spatial-objects/:id/surface`
   *
   * Returns this object's own surface configuration and every
   * configured surface belonging to it, so an editor opened on a
   * building can list its facades without walking the child rows.
   */
  async getSurfaceConfig(id: string): Promise<{
    config: SpatialObjectSurfaceConfig | null;
    surfaces: SpatialSurfaceSummary[];
  }> {
    const json = await this.request<{
      config: SpatialObjectSurfaceConfig | null;
      surfaces?: SpatialSurfaceSummary[];
    }>(`/api/admin/spatial-objects/${encodeURIComponent(id)}/surface`);
    return { config: json.config ?? null, surfaces: json.surfaces ?? [] };
  }

  /** `PUT /api/admin/spatial-objects/:id/surface` — partial update. */
  async putSurfaceConfig(
    id: string,
    patch: SpatialObjectSurfaceConfigPatch,
  ): Promise<SpatialObjectSurfaceConfig> {
    const json = await this.request<{ config: SpatialObjectSurfaceConfig }>(
      `/api/admin/spatial-objects/${encodeURIComponent(id)}/surface`,
      { method: 'PUT', body: JSON.stringify(patch) },
    );
    return json.config;
  }

  /**
   * `POST /api/admin/spatial-objects/:id/media-assets`
   *
   * Attaches a recording that already exists in the GeoTrust store;
   * the bytes never travel through GeoLink.
   */
  async addMediaAsset(
    id: string,
    asset: Partial<SpatialObjectMediaAsset> & { recordingId: string },
  ): Promise<SpatialObjectMediaAsset> {
    const json = await this.request<{ asset: SpatialObjectMediaAsset }>(
      `/api/admin/spatial-objects/${encodeURIComponent(id)}/media-assets`,
      { method: 'POST', body: JSON.stringify(asset) },
    );
    return json.asset;
  }

  /** `DELETE /api/admin/spatial-objects/:id/media-assets/:assetId` — detaches the pointer only. */
  async deleteMediaAsset(id: string, assetId: string): Promise<{ deleted: boolean }> {
    const json = await this.request<{ deleted?: boolean }>(
      `/api/admin/spatial-objects/${encodeURIComponent(id)}/media-assets/${encodeURIComponent(assetId)}`,
      { method: 'DELETE' },
    );
    return { deleted: !!json.deleted };
  }

  // ─── Execution rules (auto-fill dropdown source) ──────────────────

  /**
   * `GET /api/contracts/rules` — list rules scoped to the calling key.
   *
   * The integration guide says this endpoint joins each rule with its
   * underlying contract, so each row carries `contract_address`,
   * `contract_name`, and `function_name` — the admin UI uses these to
   * auto-fill `linked_contract_address` / `linked_contract_function`
   * when a rule is selected.
   */
  async listExecutionRules(opts: { publicKey?: string } = {}): Promise<ContractExecutionRuleSummary[]> {
    const qs = new URLSearchParams();
    if (opts.publicKey) qs.set('public_key', opts.publicKey);
    const path = `/api/contracts/rules${qs.toString() ? `?${qs.toString()}` : ''}`;

    type Envelope = { rules?: ContractExecutionRuleSummary[]; data?: ContractExecutionRuleSummary[] };
    const json = await this.request<Envelope | ContractExecutionRuleSummary[]>(path, { preferConsumerKey: true });
    if (Array.isArray(json)) return json;
    return json.rules ?? json.data ?? [];
  }
}

/** Singleton used by the admin UI. */
export const geoLinkSpatialObjectsAdminClient = new GeoLinkSpatialObjectsAdminClient();
export type { GeoLinkSpatialObjectsAdminClient };
